// ============================================================
// saveBack - where each person saved "as they stand" lands in the GM's library.
//
// The end-of-session save (the characters export's "Save to library as they
// stand") wrote only the session's own list until 2026-11-04; the user's
// ruling: each person also versions the library asset they came from, as
// the one-person save does. Several bodies built from one sheet (three
// Sicarians) are separate people: the first versions the original, each
// other one gets an asset of its OWN beside it, named by table name, which a
// later save versions again rather than making another.
//
// Pure. The saving is `useShellDomainAssets`.
// ============================================================
import type { Character } from '../character/types';
import type { Origin } from './types';

/** One body to save: its library entry (`standingEntries`), and the roster sheet it was built from. */
export interface SaveBackBody {
  entry: Character;
  sheet: Character;
}

type SaveBackPlace =
  /** The next version of this asset. */
  | { kind: 'version'; origin: Origin }
  /** A new asset in the same folder as this one. */
  | { kind: 'beside'; of: Origin }
  /** A new asset in "From session <code>": nobody in the library to stand beside. */
  | { kind: 'session' };

export interface SaveBack {
  entry: Character;
  place: SaveBackPlace;
}

/** The origin the session's copy under `id` points at: what the last save (or the copy-in) left there. */
const copyOrigin = (sessionSheets: readonly Character[], id: string): Origin | undefined =>
  sessionSheets.find((s) => s.id === id)?.origin;

/** Where one extra body lands: its own asset when a save made one, else beside the original, else the session's. */
function extraPlace(own: Origin | undefined, original: Origin | undefined): SaveBackPlace {
  // An extra's session copy written before 2026-11-04 carries the ORIGINAL's origin, copied with the sheet: that is
  // where to stand beside, never what to version, or two Sicarians would overwrite one asset.
  if (own && own.assetId !== original?.assetId) return { kind: 'version', origin: own };
  return original ? { kind: 'beside', of: original } : { kind: 'session' };
}

/**
 * Where each body lands. A body is the sheet's own (its entry keeps the sheet's id) or an extra one (an id of its
 * own, `standingEntries`). The sheet's origin is read off the session's copy first - a save points that copy at the
 * version it made - and off the roster's sheet when the session holds none.
 */
export function saveBackPlan(bodies: readonly SaveBackBody[], sessionSheets: readonly Character[]): SaveBack[] {
  return bodies.map(({ entry, sheet }) => {
    const original = copyOrigin(sessionSheets, sheet.id) ?? sheet.origin;
    if (entry.id !== sheet.id) return { entry, place: extraPlace(copyOrigin(sessionSheets, entry.id), original) };
    return { entry, place: original ? { kind: 'version', origin: original } : { kind: 'session' } };
  });
}
